import React from "react";
import { useFormContext } from "react-hook-form";
import { CodedProperty, FormDropdown, FormShimmer, mapToDropdownOption, Shimmer, Stack } from "sprint-common-web";

import { CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID } from "../../../../../../../common/api/master-api/common/consts";
import { RecordType } from "../../../../../../../common/types/RecordType";
import { CloudFileAssociationFormState } from "../../context/CloudFileAssociationContext";
import { useCloudFileAssociationSection } from "../../context/useCloudFileAssociationSection";
import { useCloudFileAssociationLanguageOptions } from "./useCloudFileAssociationLanguageOptions";

export const CloudFileAssociationLanguageForm = (): JSX.Element => {
    const { cloudFileItem, isEditDisabled, selectedLanguages, setSelectedLanguages } = useCloudFileAssociationSection();
    const { control, errors } = useFormContext<CloudFileAssociationFormState>();

    const {
        dubbings,
        areDubbingsLoading,
        dynamicSubtitles,
        areDynamicSubtitlesLoading,
        embeddedSubtitles,
        areEmbeddedSubtitlesLoading
    } = useCloudFileAssociationLanguageOptions();

    if (!cloudFileItem) return <Shimmer />;

    const { recordType } = cloudFileItem;

    const isSoundtrackAssignable = isVideoCloudFile(recordType) || isAudioCloudFile(recordType);

    const isSubtitleAssignable = isVideoCloudFile(recordType) || isSubtitleCloudFile(recordType);
    const subtitleOptions = isVideoCloudFile(recordType)
        ? mapToOptions(embeddedSubtitles)
        : mapToOptions(dynamicSubtitles);
    const areSubtitlesLoading = isVideoCloudFile(recordType) ? areEmbeddedSubtitlesLoading : areDynamicSubtitlesLoading;

    const defaultSubtitleLanguage = isVideoCloudFile(recordType) ? CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID : null;

    return (
        <Stack>
            {isSoundtrackAssignable && (
                <FormShimmer isLoading={areDubbingsLoading}>
                    <FormDropdown
                        options={mapToOptions(dubbings)}
                        label="Soundtrack"
                        name="selectedLanguages.soundtrackLanguageId"
                        testId="soundtrack-language-dropdown"
                        disabled={isEditDisabled}
                        onChange={(v) =>
                            setSelectedLanguages({ ...selectedLanguages, soundtrackLanguageId: v || null })
                        }
                        control={control}
                        errors={errors}
                    />
                </FormShimmer>
            )}

            {isSubtitleAssignable && (
                <FormShimmer isLoading={areSubtitlesLoading}>
                    <FormDropdown
                        options={subtitleOptions}
                        label="Subtitle"
                        name="selectedLanguages.subtitleLanguageId"
                        disabled={isEditDisabled}
                        testId="subtitle-language-dropdown"
                        onChange={(v) =>
                            setSelectedLanguages({
                                ...selectedLanguages,
                                subtitleLanguageId: v || defaultSubtitleLanguage
                            })
                        }
                        control={control}
                        errors={errors}
                    />
                </FormShimmer>
            )}
        </Stack>
    );
};

const mapToOptions = (items: CodedProperty[]) => items.map((i) => mapToDropdownOption(i));

export const isVideoCloudFile = (recordType: RecordType): boolean => recordType === RecordType.Video;

export const isAudioCloudFile = (recordType: RecordType): boolean => recordType === RecordType.Audio;

export const isSubtitleCloudFile = (recordType: RecordType): boolean => recordType === RecordType.Subtitle;
